import React, { useCallback, useState } from "react";
import { Button, Popconfirm, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import {
  AudioOutlined,
  AudioMutedOutlined,
  DisconnectOutlined,
  LinkOutlined,
  MutedOutlined,
  ReloadOutlined,
  SoundOutlined,
} from "@ant-design/icons";
import { usePeerConnection } from "features/robot-teleop/hooks/usePeerConnection";
import { useNotification } from "shared/contexts/UserNotificationProvider";
import { PEER_STATES } from "features/robot-teleop/webrtc/peerStates";

/**
 * PeerTileControls — per-peer buttons on a tile of the ConnectionManager-based
 * control page:
 *   - connect / disconnect / reconnect of this peer; disconnect and reconnect
 *     drop a live link, so they ask for confirmation first. A manual disconnect
 *     sticks: the peer is left alone by auto-reconnect until the user connects
 *     it again.
 *   - "my microphone": send the browser microphone to this peer, shown when the
 *     peer has a speaker (per-peer: other peers keep their own setting)
 *   - "robot audio": receive this peer's microphone, shown when the peer has one
 *     (mutes locally at once and asks the robot to stop sending)
 *
 * Compact variant (audio-only chips) renders inline instead of as an overlay.
 */
const PeerTileControls = ({ peerId, compact = false }) => {
  const { t } = useTranslation();
  const {
    state, isConnected, isConnecting, hasSpeaker, hasMicrophone,
    microphoneEnabled, remoteAudioEnabled,
    connect, disconnect, reconnect, setMicrophoneEnabled, setRemoteAudioEnabled,
  } = usePeerConnection(peerId);
  const { notifyError } = useNotification();
  const [micBusy, setMicBusy] = useState(false);

  const live = isConnected || isConnecting;

  const toggleMicrophone = useCallback(async () => {
    setMicBusy(true);
    try {
      await setMicrophoneEnabled(!microphoneEnabled);
    } catch (err) {
      notifyError(t("teleop.peer.controls.micUnavailable", { message: err?.message || String(err) }));
    } finally {
      setMicBusy(false);
    }
  }, [microphoneEnabled, setMicrophoneEnabled, notifyError, t]);

  const toggleRemoteAudio = useCallback(() => {
    setRemoteAudioEnabled(!remoteAudioEnabled);
  }, [remoteAudioEnabled, setRemoteAudioEnabled]);

  return (
    <div className={`peer-tools ${compact ? "compact" : ""}`} data-peer-id={peerId}>
      {live ? (
        <>
          <Popconfirm
            title={t("teleop.peer.controls.reconnectTitle", { peerId })}
            description={t("teleop.peer.controls.reconnectDescription")}
            okText={t("teleop.peer.controls.yesReconnect")}
            cancelText={t("teleop.peer.controls.cancel")}
            placement="bottomRight"
            disabled={isConnecting}
            destroyOnHidden
            onConfirm={reconnect}
          >
            <Button
              size="small"
              type="text"
              className="peer-tool"
              icon={<ReloadOutlined />}
              disabled={isConnecting}
              title={isConnecting ? t("teleop.peer.controls.connectingHint") : t("teleop.peer.controls.reconnectHint")}
              aria-label={t("teleop.peer.controls.reconnect")}
            />
          </Popconfirm>
          <Popconfirm
            title={t("teleop.peer.controls.disconnectTitle", { peerId })}
            description={t("teleop.peer.controls.disconnectDescription")}
            okText={t("teleop.peer.controls.yesDisconnect")}
            okButtonProps={{ danger: true }}
            cancelText={t("teleop.peer.controls.cancel")}
            placement="bottomRight"
            destroyOnHidden
            onConfirm={disconnect}
          >
            <Button
              size="small"
              type="text"
              danger
              className="peer-tool"
              icon={<DisconnectOutlined />}
              title={t("teleop.peer.controls.disconnectHint")}
              aria-label={t("teleop.peer.controls.disconnect")}
            />
          </Popconfirm>
        </>
      ) : (
        <Tooltip title={state === PEER_STATES.FAILED ? t("teleop.peer.controls.retryHint") : t("teleop.peer.controls.connectHint")}>
          <Button
            size="small"
            type="text"
            className="peer-tool"
            icon={<LinkOutlined />}
            onClick={connect}
            aria-label={t("teleop.peer.controls.connect")}
          />
        </Tooltip>
      )}

      {hasSpeaker && (
        <Tooltip title={microphoneEnabled ? t("teleop.peer.controls.micOnHint") : t("teleop.peer.controls.micOffHint")}>
          <Button
            size="small"
            type={microphoneEnabled ? "primary" : "text"}
            className={`peer-tool ${microphoneEnabled ? "on" : ""}`}
            icon={microphoneEnabled ? <AudioOutlined /> : <AudioMutedOutlined />}
            loading={micBusy}
            onClick={toggleMicrophone}
            aria-label={microphoneEnabled ? t("teleop.peer.controls.micStop") : t("teleop.peer.controls.micSend")}
            aria-pressed={microphoneEnabled}
          />
        </Tooltip>
      )}

      {hasMicrophone && (
        <Tooltip title={remoteAudioEnabled ? t("teleop.peer.controls.robotAudioOnHint") : t("teleop.peer.controls.robotAudioOffHint")}>
          <Button
            size="small"
            type={remoteAudioEnabled ? "primary" : "text"}
            className={`peer-tool ${remoteAudioEnabled ? "on" : ""}`}
            icon={remoteAudioEnabled ? <SoundOutlined /> : <MutedOutlined />}
            onClick={toggleRemoteAudio}
            aria-label={remoteAudioEnabled ? t("teleop.peer.controls.robotAudioMute") : t("teleop.peer.controls.robotAudioListen")}
            aria-pressed={remoteAudioEnabled}
          />
        </Tooltip>
      )}
    </div>
  );
};

export default React.memo(PeerTileControls);
